import React, {createContext, useContext, useEffect, useState} from 'react';
import {signInAnonymously, onAuthStateChanged, User} from 'firebase/auth';
import {auth} from '@src/firebase';
import {useFirebaseRemoteConfig} from '../FirebaseRemoteConfigProvider/FirebaseRemoteConfigProvider';

interface AnonymousAuthContextType {
  isAnonymous: boolean;
  user: User | null;
  isInitialized: boolean;
  signInWithAuthentication: () => void;
}

const AnonymousAuthContext = createContext<AnonymousAuthContextType>({
  isAnonymous: false,
  user: null,
  isInitialized: false,
  signInWithAuthentication: () => {},
});

export const useAnonymousAuth = () => {
  const context = useContext(AnonymousAuthContext);
  if (!context) {
    throw new Error(
      'useAnonymousAuth must be used within AnonymousAuthProvider',
    );
  }
  return context;
};

interface AnonymousAuthProviderProps {
  children: React.ReactNode;
}

const AnonymousAuthProvider: React.FC<AnonymousAuthProviderProps> = ({
  children,
}) => {
  const {anonymousAuthEnabled, isLoading: remoteConfigLoading} =
    useFirebaseRemoteConfig();
  const [user, setUser] = useState<User | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);

  const signInWithAuthentication = () => {
    // Cette fonction sera appelée pour ouvrir la modale d'authentification
    // et convertir l'utilisateur anonyme en utilisateur authentifié
    console.log(
      "Ouverture de la modale d'authentification pour utilisateur anonyme",
    );
    // Cette fonction est maintenant utilisée par le hook useAnonymousAuthModal
    // L'ouverture de la modale est gérée par ce hook
  };

  useEffect(() => {
    if (remoteConfigLoading) return;

    const unsubscribe = onAuthStateChanged(auth, async user => {
      setUser(user);

      if (user) {
        setIsAnonymous(user.isAnonymous);
        setIsInitialized(true);
      } else if (anonymousAuthEnabled) {
        // Si l'authentification anonyme est activée et qu'aucun utilisateur n'est connecté,
        // créer automatiquement un utilisateur anonyme
        try {
          console.log("🔐 Création automatique d'un utilisateur anonyme...");
          const userCredential = await signInAnonymously(auth);
          console.log('✅ Utilisateur anonyme créé:', userCredential.user.uid);
          setUser(userCredential.user);
          setIsAnonymous(true);
          setIsInitialized(true);
        } catch (error) {
          console.error(
            "❌ Erreur lors de la création de l'utilisateur anonyme:",
            error,
          );
          setIsInitialized(true);
        }
      } else {
        setIsInitialized(true);
      }
    });

    return () => unsubscribe();
  }, [anonymousAuthEnabled, remoteConfigLoading]);

  return (
    <AnonymousAuthContext.Provider
      value={{
        isAnonymous,
        user,
        isInitialized,
        signInWithAuthentication,
      }}>
      {children}
    </AnonymousAuthContext.Provider>
  );
};

export default AnonymousAuthProvider;
